import { Row, Col, Typography, Table, Button, message } from "antd";
import { generateHeaders } from "common/utils";
import { AppPageHeader } from "components/AppPageHeader";
import { PartnerVariant, usePartnerServicesConfig } from "hooks/usePartnerServicesConfig";
import { useDocumentTitle } from "hooks/usePageTitle";
import { useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";

const MESSAGE_KEY = "SAVE_ALTER_EARNINGS";

const SaveAlterEarningsPage = ({ variant = "ppd" }: { variant?: PartnerVariant }) => {
  useDocumentTitle("Save Alter Earnings");
  const config = usePartnerServicesConfig(variant);
  const {
    dispatch,
    processingAlterEarnings,
    status,
    error,
    partnerRoutes,
    saveAlterEarningsThunk,
    cancelAlterEarningsThunk,
    removeAlterEarningsAction,
  } = config;

  const navigate = useNavigate();

  useEffect(() => {
    const { timeUploaded } = processingAlterEarnings;
    if (timeUploaded) {
      const uploadedTime = new Date(timeUploaded).getTime();
      const currentTime = new Date().getTime();
      const minsPassed = (currentTime - uploadedTime) / 1000 / 60;
      if (minsPassed >= 15) dispatch(removeAlterEarningsAction());
    }
    if (!Object.keys(processingAlterEarnings).length) {
      navigate(`/${partnerRoutes.UPLOAD_ALTER_EARNINGS}`);
    }
  }, [processingAlterEarnings, navigate, dispatch, removeAlterEarningsAction, partnerRoutes]);

  useEffect(() => {
    if (status === "loading") {
      message.loading({ content: "Saving Alter Earnings...", key: MESSAGE_KEY });
    } else if (status === "success") {
      message.success({ content: "Alter Earnings saved successfully!", duration: 5, key: MESSAGE_KEY });
      navigate(`/${partnerRoutes.EARNING_PAYOUTS}`);
    } else if (status === "cancelling") {
      message.loading({ content: "Cancelling Alter Earnings...", key: MESSAGE_KEY });
    } else if (status === "error") {
      message.error({ content: error, key: MESSAGE_KEY });
    }
    return () => { message.destroy(); };
  }, [status, error, navigate, partnerRoutes]);

  const { Text } = Typography;
  const { headerMapping } = processingAlterEarnings;

  const generateMemoizedHeaders = useMemo(
    () => generateHeaders(headerMapping),
    [headerMapping]
  );

  let content;
  if (!Object.keys(processingAlterEarnings).length) {
    content = <Text>Nothing to show here!</Text>;
  } else {
    const headers = generateMemoizedHeaders || {};
    const data = processingAlterEarnings.successRows?.map((row, i) => ({ ...row, key: `data-${i}` }));
    const columns = Object.keys(headers).map((key) => ({
      key,
      title: headers[key],
      dataIndex: key,
    }));
    content = (
      <Table
        size="small"
        columns={columns}
        dataSource={data}
        rowKey="key"
        scroll={{ y: 500, x: "100vh" }}
        summary={() => (
          <Table.Summary fixed="bottom">
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={24}>
                <Row>
                  <Col>
                    <Button
                      type="primary"
                      onClick={() => dispatch(saveAlterEarningsThunk())}
                      loading={status === "loading"}
                      disabled={status === "loading"}
                    >
                      Save
                    </Button>
                  </Col>
                  <Col style={{ marginLeft: "20px" }}>
                    <Button
                      onClick={() => dispatch(cancelAlterEarningsThunk())}
                      loading={status === "cancelling"}
                      disabled={status === "loading"}
                      danger
                    >
                      Cancel
                    </Button>
                  </Col>
                </Row>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    );
  }

  return (
    <>
      <Row>
        <Col>
          <AppPageHeader title="Save Alter Earnings" />
        </Col>
      </Row>
      <Row>
        <Col xs={24}>{content}</Col>
      </Row>
    </>
  );
};

export default SaveAlterEarningsPage;
